/**
 * AuthoringPreviewPanel.tsx — what the Engine decided, before it is applied.
 *
 * §12 requires a preview that a human can review, and the thing worth reviewing
 * is not the gesture the user made — it is the RESOLUTION: which exact ports the
 * Engine chose, which coordinate it settled on, which line properties it
 * carried across a split. This panel shows those, then the normalized
 * primitives, then the canonical diff.
 *
 * When the Engine refuses, the refusal is shown verbatim. An ambiguity with
 * candidates is a question the user can answer, not an error to swallow.
 */

import { Empty, Panel } from "../../components/Panel";
import type { AuthoringPreview } from "../../types/contract";

export function AuthoringPreviewPanel({ preview, onApply, onDiscard, busy, applied }: {
  preview: AuthoringPreview | undefined;
  onApply?: () => void;
  onDiscard?: () => void;
  busy?: boolean;
  applied?: boolean;
}): JSX.Element | null {
  if (!preview) return null;

  return (
    // THE STAGE HOOKS, and why this component needs them.
    //
    // This panel renders the Engine's verdict for a GESTURE, and it carries no
    // `data-stage` attribute — so a real browser acceptance run that drives the
    // canvas had nothing to wait for and nothing to read. It timed out looking for
    // a stage the product never rendered, and the run reported the protocol as NOT
    // RUN rather than as the thing that was actually true: the Engine had
    // ACCEPTED and the page said so.
    //
    // The hook names the stage this panel is at — `stage-review` for an answer
    // under consideration, `stage-readback` once it has been applied — so a browser
    // can wait for the verdict and read it, exactly as it does for the capability
    // workbench. The content is the Engine's; only the addressability is added.
    <div
      data-stage={applied ? "stage-readback" : "stage-review"}
      data-verdict={preview.ok ? "accepted" : "refused"}
      data-error-count={preview.errors.length}
      data-canonical-diff-count={preview.canonicalDiff.length}
    >
    <Panel
      title={applied ? "Applied" : "Engine preview"}
      tone={preview.ok ? "default" : "error"}
      actions={
        <>
          {onApply && preview.ok && !applied && (
            <button type="button" onClick={onApply} disabled={busy}>Apply to change set</button>
          )}
          {onDiscard && <button type="button" className="ghost" onClick={onDiscard} disabled={busy}>Close</button>}
        </>
      }
    >
      {preview.errors.length > 0 && (
        <ul className="findings">
          {preview.errors.map((message, index) => (
            <li key={index} className="severity-error"><span>{message}</span></li>
          ))}
        </ul>
      )}
      {preview.warnings.length > 0 && (
        <ul className="findings">
          {preview.warnings.map((message, index) => (
            <li key={index} className="severity-warning"><span>{message}</span></li>
          ))}
        </ul>
      )}

      <h3>Resolutions</h3>
      {preview.resolutions.length === 0 ? <Empty>Nothing needed resolving.</Empty> : (
        <ul className="resolutions">
          {preview.resolutions.map((resolution, index) => (
            <li key={`${resolution.operationId}-${index}`}>
              <code>{resolution.kind}</code>
              <span className="muted">{resolution.operationId}</span>
              <pre>{JSON.stringify(resolution.detail, null, 2)}</pre>
            </li>
          ))}
        </ul>
      )}

      <h3>Normalized operations</h3>
      {preview.normalizedOperations.length === 0 ? <Empty>None.</Empty> : (
        <ol className="normalized">
          {preview.normalizedOperations.map(operation => (
            <li key={operation.operationId}>
              <code>{operation.type}</code>
              <pre>{JSON.stringify(operation.command, null, 2)}</pre>
            </li>
          ))}
        </ol>
      )}

      <h3>Canonical files touched</h3>
      {preview.canonicalDiff.length === 0 ? <Empty>None.</Empty> : (
        <ul className="diff">
          {preview.canonicalDiff.map(entry => (
            <li key={entry.resource}>
              <code>{entry.resource}</code>
              <span className="muted">{entry.before === null ? "created" : entry.after === null ? "removed" : "modified"}</span>
            </li>
          ))}
        </ul>
      )}

      <p data-stage="gesture-verdict">
        {preview.ok ? (
          <span className="good" data-stage="gesture-accepted">
            The Engine accepted this: its own <code>ok</code> is true, and it reports{" "}
            {preview.canonicalDiff.length} canonical file{preview.canonicalDiff.length === 1 ? "" : "s"} it would
            change. Nothing above was calculated in this browser.
          </span>
        ) : (
          <span className="bad" data-stage="gesture-refused">
            The Engine did not accept this: its own <code>ok</code> is not true. Its diagnostics are above,
            verbatim.
          </span>
        )}
      </p>

      {applied && (
        <p data-stage="gesture-applied">
          <span className="good">
            Applied to the change set: the change is <strong>recorded in the change set</strong>, not published —
            publication is a separate reviewed step, and this browser does not perform it. The Engine documents{" "}
            <code>applied</code> as always false on this route, so recording is what the change set readback shows,
            not that flag.
          </span>
        </p>
      )}
    </Panel>
    </div>
  );
}
